import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import Brightcove from './js/brightcove';
import Analytics from './js/analytics';
import AnimatedGif from './js/animatedGif';
import DraggableNav from './js/draggableNav';

export default class Facts {
  constructor() {
    this.example = 'example';

    // start position for animations
    this.startPos = 'top top';
    // this.endPos = 'bottom top';
    // this.endPos = () => `bottom+=${window.innerHeight * 2} top`;

    // end position for animations
    // this.endPos = () => '+=250%';
    // this.endPos = () => '+=200%';
    // this.endPos = 'bottom top-=1000px';
    this.endPos = 'bottom top-=1000px';

    // the number of seconds it takes smooth scrubbing "catch up" to the scrollbar
    this.scrub = 7;

    // animation length
    this.duration = 12;


    // animation length for the years
    this.yearDuration = 1;

    // animation length for the headline and body
    this.textDuration = 10;

    // show start and end markers for each animation
    this.markers = false;

    // pause in seconds before scrolling to next section
    this.scrollTrigerStutter = 10;

    // ease for animations
    this.ease = 'power1.out';

    // pin sections until animations are complete
    this.pin = true;

    // anticipate pin
    this.anticipatePin = 2;

    //  If true, the animation associated with the ScrollTrigger will have its invalidate() method called whenever a refresh() occurs
    this.invalidateOnRefresh = true;

    // if true, it will force the current ScrollTrigger's animation to completion if you leave its trigger area faster than a certain velocity
    this.fastScrollEnd = false;

    // brightcove instance
    this.brightcove = new Brightcove();

    this.mediumWidth = 600;
  }

  init() {
    this.example = 'initialized';

    // trigger builds and animation
    this.initGsap();
    this.initAnimation();
    this.initAnalytics();
    this.loadGifs();
    this.addAnimationListeners();
    this.initNav();
  }

  initNav() {
    const d = new DraggableNav();
    d.init();
  }

  loadGifs() {
    document.querySelectorAll('.gif-container').forEach((gif) => {
      /*eslint-disable */
      new AnimatedGif(gif);
      /* eslint-enable */
    });
  }

  initAnalytics() {
    this.utag = new Analytics();
    this.utag.fireViewTag('b150-look-back');
  }

  initGsap() {
    // register scrolltrigger and scrollto plugins
    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

    // set gsap defaults
    gsap.defaults({
      // ease: this.ease,
      duration: this.duration,
    });

    // set scrolltrigger defaults
    ScrollTrigger.defaults({
      pin: this.pin,
      start: this.startPos,
      end: this.endPos,
      markers: this.markers,
      scrub: this.scrub,
      anticipatePin: this.anticipatePin,
      invalidateOnRefresh: this.invalidateOnRefresh,
      // fastScrollEnd: this.fastScrollEnd,
      // onRefresh: self => self.progress === 1 && self.animation.progress(1),
      toggleActions: 'play none resume reverse',
    });

    // normalize scrollbar
    ScrollTrigger.normalizeScroll(true);
  }

  // this updates the year based on scroll percentage within that section
  updateYearProgress(elem, start, stop, progress) {
    if (elem) {
      elem.innerHTML = this.calculateYear(start, stop, progress);
    }
  }

  // calculate year based on progress of scroll section
  calculateYear(start, stop, progress) {
    return start + Math.round((stop - start) * progress);
  }

  setSectionHeight() {
    document.querySelectorAll('.tmln-section').forEach((section) => {
      this.checkSectionHeight(window.innerWidth, section);
    });
  }

  checkSectionHeight(windowInnerWidth, section) {
    if (windowInnerWidth >= this.mediumWidth) {
      section.style.minHeight = `${window.innerHeight}px`;
    } else {
      section.style.minHeight = 0;
    }
  }

  addAnimationListeners() {
    // scrolltrigger listeners

    window.addEventListener('orientationchange', () => this.setOrientationChange(), false);

    // ScrollTrigger.addEventListener('refreshInit', () => console.log(`150-scroll-trigger refreshInit: ${this.getScrollPercentage()}`));
    // ScrollTrigger.addEventListener('refresh', () => console.log(`150-scroll-trigger refresh: ${this.getScrollPercentage()}`));
    // ScrollTrigger.addEventListener('revert', () => console.log(`150-scroll-trigger revert: ${this.getScrollPercentage()}`));
  }

  setOrientationChange() {
    ScrollTrigger.refresh();
    setTimeout(() => ScrollTrigger.refresh(), 200);
  }

  getScrollPercentage() {
    const h = document.documentElement;
    const b = document.body;
    const st = 'scrollTop';
    const sh = 'scrollHeight';
    /*eslint-disable */
    return (h[st] || b[st]) / ((h[sh] || b[sh]) - h.clientHeight) * 100;
    /* eslint-enable */
  }

  setMobileAnimation() {
    // these are the animations for small only
    // we kill any previous animations using clearProps: true before setting new ones

    // intro
    gsap.set('.ann-feature', { clearProps: true });
    gsap.set('.ann-intro-headline', { clearProps: true });
    gsap.set('.intro-image-inner', { clearProps: true });
    gsap.set('.intro-image-inner-mobile', { clearProps: true });

    gsap.timeline({
      scrollTrigger: {
        trigger: '.ann-feature', pin: false, start: 'top top+=180px',
      },
    })
      .fromTo('.intro-image-inner-mobile', { scale: 0.7, opacity: 0.8 }, { scale: 2, opacity: 1, duration: 0.5 });


    // a look back
    gsap.set('.tmln-section-0 h2', { clearProps: true });
    gsap.set('.tmln-section-0 p', { clearProps: true });
    gsap.set('.tmln-section-0', { clearProps: true });

    gsap.timeline({
      scrollTrigger: {
        trigger: '.tmln-section-0', start: 'top bottom', end: 'bottom top', pin: false,
      },
    })
      .fromTo('.tmln-section-0 h2', { xPercent: 150 }, { xPercent: -80, duration: this.duration * 4 });


    // how it started
    gsap.set('.tmln-section-1', { clearProps: true });
    gsap.set('.tmln-section-1 .text-cell', { clearProps: true });
    gsap.set('.tmln-section-1 .bg-image', { clearProps: 'opacity' });
    gsap.set('.tmln-section-1 .tmln-year span', { clearProps: true });
    gsap.set('.tmln-section-1 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-1 p', { clearProps: true });
    gsap.set('.tmln-section-1 .bloom-bros', { clearProps: true });
    gsap.set('.tmln-section-1 .script-b', { clearProps: true });

    gsap.timeline({
      scrollTrigger:
      {
        trigger: '.tmln-section-1',
        start: 'top bottom',
        end: 'bottom center',
        pin: false,
      },
    })
      .fromTo('.tmln-section-1 .script-b', { x: ' 100vw' }, { x: 0, duration: 10 }, '<+30%');

    // making moves
    gsap.set('.tmln-section-2', { clearProps: true });
    gsap.set('.tmln-section-2 .col-1 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-2 .col-2 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-2 .col-1 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-2 .col-2 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-2 .col-1 p', { clearProps: true });
    gsap.set('.tmln-section-2 .col-1 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-2 .col-1 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-2 .col-2 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-2 .col-2 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-2 .col-2 p', { clearProps: true });

    // 1930's
    gsap.set('.tmln-section-3', { clearProps: true });
    gsap.set('.tmln-section-3 .tmln-year-cell', { clearProps: true });
    document.querySelectorAll('.tmln-section-3 .tmln-group').forEach((section) => {
      gsap.set(section, { clearProps: true });
    });

    // a new business model
    gsap.set('.tmln-section-4', { clearProps: true });
    gsap.set('.tmln-section-4 .col-1 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-4 .col-2 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-4 .col-1 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-4 .col-2 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-4 .col-1 p', { clearProps: true });
    gsap.set('.tmln-section-4 .col-1 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-4 .col-1 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-4 .col-2 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-4 .col-2 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-4 .col-2 p', { clearProps: true });

    // big brown bags
    gsap.set('.tmln-section-5', { clearProps: true });
    gsap.set('.tmln-section-5 .tmln-year span', { clearProps: true });
    gsap.set('.tmln-section-5 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-5 p', { clearProps: true });
    document.querySelectorAll('.tmln-section-5 .bag-item').forEach((section) => {
      gsap.set(section, { clearProps: true });
    });

    // designers
    gsap.set('.tmln-section-6', { clearProps: true });
    gsap.set('.tmln-section-6 .tmln-year span', { clearProps: true });
    gsap.set('.tmln-section-6 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-6 p', { clearProps: true });
    gsap.set('.tmln-section-6 .tmln-brand span', { clearProps: true });

    // 1970's
    gsap.set('.tmln-section-7', { clearProps: true });
    gsap.set('.tmln-section-7 .tmln-year-cell', { clearProps: true });
    document.querySelectorAll('.tmln-section-7 .tmln-group').forEach((section) => {
      gsap.set(section, { clearProps: true });
    });

    // 1980's
    gsap.set('.tmln-section-9', { clearProps: true });
    gsap.set('.tmln-section-9 .col-1 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-9 .col-2 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-9 .col-1 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-9 .col-2 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-9 .col-1 .barbie-box', { clearProps: true });
    gsap.set('.tmln-section-9 .col-1 p', { clearProps: true });
    gsap.set('.tmln-section-9 .col-1 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-9 .col-1 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-9 .col-2 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-9 .col-2 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-9 .col-2 p', { clearProps: true });

    // warhol
    gsap.set('.tmln-section-10', { clearProps: true });
    gsap.set('.tmln-section-10 .tmln-year span', { clearProps: true });
    gsap.set('.tmln-section-10 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-10 p', { clearProps: true });
    gsap.set('.tmln-section-10 .warhol-b-grid', { clearProps: true });
    gsap.set('.tmln-section-10 .warhol-headshot', { clearProps: true });
    gsap.set('.tmln-section-10 .warhol-quote', { clearProps: true });

    gsap.timeline({
      scrollTrigger:
    {
      trigger: '.tmln-section-10',
      start: 'top bottom',
      end: 'top center',
      pin: false,
    },
    })
      .fromTo('.tmln-section-10 .warhol-photo-2', { x: '100vw', rotation: 120 }, { x: 0, rotation: 5, duration: 2 });


    // 1990's
    gsap.set('.tmln-section-12', { clearProps: true });
    gsap.set('.tmln-section-12 .col-1 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-12 .col-2 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-12 .col-1 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-12 .col-2 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-12 .col-1 .img-mask .ann-bc-video', { clearProps: true });
    gsap.set('.tmln-section-12 .col-2 .img-mask .ann-bc-video', { clearProps: true });
    gsap.set('.tmln-section-12 .col-1 p', { clearProps: true });
    gsap.set('.tmln-section-12 .col-1 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-12 .col-1 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-12 .col-2 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-12 .col-2 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-12 .col-2 p', { clearProps: true });


    // 2010's
    gsap.set('.tmln-section-11', { clearProps: true });
    gsap.set('.tmln-section-11 .tmln-year-cell', { clearProps: true });
    document.querySelectorAll('.tmln-section-11 .tmln-group').forEach((section) => {
      gsap.set(section, { clearProps: true });
    });


    // in good company
    gsap.set('.tmln-section-13', { clearProps: true });
    gsap.set('.tmln-section-13 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-13 p', { clearProps: true });
    gsap.set('.tmln-section-13 .catalog', { clearProps: true });
    gsap.set('.tmln-section-13 .ann-bc-video', { clearProps: true });
    gsap.set('.tmln-section-13 .nintendo', { clearProps: true });
    gsap.set('.tmln-section-13 .helmets', { clearProps: true });
  }

  setMediumAnimation() {
    // these are the animations for medium and large
    // we kill any previous animations using clearProps: true before setting new ones

    // intro
    gsap.set('.ann-feature', { clearProps: true });
    gsap.set('.ann-intro-headline', { clearProps: true });
    gsap.set('.intro-image-inner', { clearProps: true });
    gsap.set('.intro-image-inner-mobile', { clearProps: true });

    gsap.timeline({ scrollTrigger: { trigger: '.ann-feature' } })
      .to('body', { duration: 2 })
      .fromTo('.ann-intro-headline', { yPercent: 0 }, { yPercent: -300, duration: this.duration * 2 })
      .fromTo('.intro-image-inner', { scale: 0.5, opacity: 0.8 }, { scale: 1, opacity: 1 }, '<+=20%')
      .to('body', { duration: 20 });


    // a look back
    gsap.set('.tmln-section-0 h2', { clearProps: true });
    gsap.set('.tmln-section-0 p', { clearProps: true });
    gsap.set('.tmln-section-0', { clearProps: true });

    gsap.timeline({ scrollTrigger: { trigger: '.tmln-section-0', end: 'bottom top' } })
      .to('body', { duration: this.scrollTrigerStutter })
      .fromTo('.tmln-section-0', { background: '#D9208C' }, { background: '#000' })
      .to('body', { duration: this.scrollTrigerStutter });

    gsap.timeline({
      scrollTrigger: {
        trigger: '.tmln-section-0', start: 'top center', end: 'bottom top', pin: false,
      },
    })
      .fromTo('.tmln-section-0 h2', { xPercent: 200 }, { xPercent: -50, duration: this.duration * 4 })
      .fromTo('.tmln-section-0 p', { yPercent: 1000 }, { yPercent: -225, duration: this.duration * 3 }, '<+=50%');


    // how it started
    gsap.set('.tmln-section-1', { clearProps: true });
    gsap.set('.tmln-section-1 .text-cell', { clearProps: true });
    gsap.set('.tmln-section-1 .bg-image', { clearProps: 'opacity' });
    gsap.set('.tmln-section-1 .tmln-year span', { clearProps: true });
    gsap.set('.tmln-section-1 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-1 p', { clearProps: true });
    gsap.set('.tmln-section-1 .bloom-bros', { clearProps: true });
    gsap.set('.tmln-section-1 .script-b', { clearProps: true });

    gsap.timeline({ scrollTrigger: { trigger: '.tmln-section-1' } })
      .set('.tmln-section-1 .text-cell', { xPercent: 0 })
      .fromTo('.tmln-section-1 .bg-image', { opacity: 0 }, { opacity: 1 })
      .fromTo('.tmln-section-1 .tmln-year span', { yPercent: -100 }, { yPercent: 0, duration: this.yearDuration }, 0)
      .fromTo('.tmln-section-1 .tmln-subhead', { xPercent: -20, opacity: 0 }, { xPercent: 0, opacity: 1, duration: this.textDuration }, '<+20%')
      .fromTo('.tmln-section-1 p', { xPercent: -20, opacity: 0 }, { xPercent: 0, opacity: 1, duration: this.textDuration }, '<+20%')
      .fromTo('.tmln-section-1 .bloom-bros', { x: '100vw' }, { x: 0 }, '<+20%')
      .fromTo('.tmln-section-1 .script-b', { x: ' 100vw' }, { x: 0, duration: 10 }, '<+30%')
      .to('body', { duration: this.scrollTrigerStutter });


    // making moves
    gsap.set('.tmln-section-2', { clearProps: true });
    gsap.set('.tmln-section-2 .col-1 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-2 .col-2 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-2 .col-1 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-2 .col-2 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-2 .col-1 p', { clearProps: true });
    gsap.set('.tmln-section-2 .col-1 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-2 .col-1 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-2 .col-2 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-2 .col-2 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-2 .col-2 p', { clearProps: true });

    gsap.timeline({ scrollTrigger: { trigger: '.tmln-section-2' } })
      .fromTo('.tmln-section-2 .col-1 .img-mask', { x: '-100vw' }, { x: 0 })
      .fromTo('.tmln-section-2 .col-1 .img-mask img', { scale: 4 }, { scale: 1 }, '<')
      .fromTo('.tmln-section-2 .col-2 .img-mask', { x: '100vw' }, { x: 0 }, '<')
      .fromTo('.tmln-section-2 .col-2 .img-mask img', { scale: 4 }, { scale: 1 }, '<')
      .fromTo('.tmln-section-2 .col-1 p', { yPercent: -100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+50%')
      .fromTo('.tmln-section-2 .col-1 .tmln-subhead', { yPercent: -100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .fromTo('.tmln-section-2 .col-1 .tmln-year', { yPercent: -100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .fromTo('.tmln-section-2 .col-2 .tmln-year', { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<')
      .fromTo('.tmln-section-2 .col-2 .tmln-subhead', { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .fromTo('.tmln-section-2 .col-2 p', { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .to('body', { duration: this.scrollTrigerStutter });


    // 1930's
    gsap.set('.tmln-section-3', { clearProps: true });
    gsap.set('.tmln-section-3 .tmln-year-cell', { clearProps: true });
    gsap.set('.tmln-section-3 .tmln-group', { clearProps: true });

    gsap.timeline({
      scrollTrigger: {
        trigger: '.tmln-section-3',
        pin: '.tmln-section-3 .tmln-year-cell',
        start: 'top top+=45px',
        end: 'bottom-=260px top',
        onUpdate: self => this.updateYearProgress(document.querySelector('.tmln-section-3 .tmln-year-large'), 1930, 1940, self.progress),
      },
    });
    document.querySelectorAll('.tmln-section-3 .tmln-group').forEach((section) => {
      gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top bottom',
          end: 'center bottom',
          pin: false,
          scrub: 1,
        },
      })
        .fromTo(section, { yPercent: 20 }, { yPercent: 0, duration: 1.5 });
    });


    // a new business model
    gsap.set('.tmln-section-4', { clearProps: true });
    gsap.set('.tmln-section-4 .col-1 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-4 .col-2 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-4 .col-1 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-4 .col-2 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-4 .col-1 p', { clearProps: true });
    gsap.set('.tmln-section-4 .col-1 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-4 .col-1 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-4 .col-2 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-4 .col-2 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-4 .col-2 p', { clearProps: true });

    gsap.timeline({ scrollTrigger: { trigger: '.tmln-section-4' } })
      .fromTo('.tmln-section-4 .col-1 .img-mask', { x: '-100vw' }, { x: 0 })
      .fromTo('.tmln-section-4 .col-1 .img-mask img', { scale: 4 }, { scale: 1 }, '<')
      .fromTo('.tmln-section-4 .col-2 .img-mask', { x: '100vw' }, { x: 0 }, '<')
      .fromTo('.tmln-section-4 .col-2 .img-mask img', { scale: 4 }, { scale: 1 }, '<')
      .fromTo('.tmln-section-4 .col-1 p', { yPercent: -100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+50%')
      .fromTo('.tmln-section-4 .col-1 .tmln-subhead', { yPercent: -100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .fromTo('.tmln-section-4 .col-1 .tmln-year', { yPercent: -100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .fromTo('.tmln-section-4 .col-2 .tmln-year', { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<')
      .fromTo('.tmln-section-4 .col-2 .tmln-subhead', { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .fromTo('.tmln-section-4 .col-2 p', { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .to('body', { duration: this.scrollTrigerStutter });


    // big brown bags
    gsap.set('.tmln-section-5', { clearProps: true });
    gsap.set('.tmln-section-5 .tmln-year span', { clearProps: true });
    gsap.set('.tmln-section-5 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-5 p', { clearProps: true });
    gsap.set('.tmln-section-5 .bag-item-1', { clearProps: true });
    gsap.set('.tmln-section-5 .bag-item-2', { clearProps: true });
    gsap.set('.tmln-section-5 .bag-item-3', { clearProps: true });
    gsap.set('.tmln-section-5 .bag-item-4', { clearProps: true });
    gsap.set('.tmln-section-5 .bag-item-5', { clearProps: true });
    gsap.set('.tmln-section-5 .bag-item-6', { clearProps: true });
    gsap.set('.tmln-section-5 .bag-item-7', { clearProps: true });
    gsap.set('.tmln-section-5 .bag-item-8', { clearProps: true });

    gsap.timeline({
      scrollTrigger: {
        trigger: '.tmln-section-5',
        end: '+=400%',
      },
    })
      .fromTo('.tmln-section-5 .tmln-year span', { yPercent: -100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.yearDuration }, 0)
      .fromTo('.tmln-section-5 .tmln-subhead', { xPercent: -20, opacity: 0 }, { xPercent: 0, opacity: 1, duration: this.textDuration }, '<+20%')
      .fromTo('.tmln-section-5 p', { xPercent: -20, opacity: 0 }, { xPercent: 0, opacity: 1, duration: this.textDuration }, '<+20%')
      .fromTo('.tmln-section-5 .bag-item-1', { y: '-100vh' }, { y: 0 }, '<+30%')
      .fromTo('.tmln-section-5 .bag-item-2', { y: '100vh' }, { y: 0 }, '<0')
      .fromTo('.tmln-section-5 .bag-item-1', { y: 0 }, { y: '100vh' }, '>+=4')
      .fromTo('.tmln-section-5 .bag-item-2', { y: 0 }, { y: '-100vh' }, '<0')
      .fromTo('.tmln-section-5 .bag-item-3', { y: '-100vh' }, { y: 0 }, '<0')
      .fromTo('.tmln-section-5 .bag-item-4', { y: '100vh' }, { y: 0 }, '<0')
      .fromTo('.tmln-section-5 .bag-item-3', { y: 0 }, { y: '100vh' }, '>+=4')
      .fromTo('.tmln-section-5 .bag-item-4', { y: 0 }, { y: '-100vh' }, '<0')
      .fromTo('.tmln-section-5 .bag-item-5', { y: '-100vh' }, { y: 0 }, '<0')
      .fromTo('.tmln-section-5 .bag-item-6', { y: '100vh' }, { y: 0 }, '<0')
      .fromTo('.tmln-section-5 .bag-item-5', { y: 0 }, { y: '100vh' }, '>+=4')
      .fromTo('.tmln-section-5 .bag-item-6', { y: 0 }, { y: '-100vh' }, '<0')
      .fromTo('.tmln-section-5 .bag-item-7', { y: '-100vh' }, { y: 0 }, '<0')
      .fromTo('.tmln-section-5 .bag-item-8', { y: '100vh' }, { y: 0 }, '<0')
      .to('body', { duration: this.scrollTrigerStutter });


    // designers
    gsap.set('.tmln-section-6', { clearProps: true });
    gsap.set('.tmln-section-6 .tmln-year span', { clearProps: true });
    gsap.set('.tmln-section-6 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-6 p', { clearProps: true });
    gsap.set('.tmln-section-6 .tmln-brand span', { clearProps: true });

    gsap.timeline({
      scrollTrigger: {
        trigger: '.tmln-section-6',
        end: '+=300%',
      },
    })
      .fromTo('.tmln-section-6 .tmln-year span', { yPercent: -100 }, { yPercent: 0, duration: this.yearDuration }, 0)
      .fromTo('.tmln-section-6 .tmln-subhead', { xPercent: 20, opacity: 0 }, { xPercent: 0, opacity: 1, duration: this.textDuration }, '<+20%')
      .fromTo('.tmln-section-6 p', { xPercent: 20, opacity: 0 }, { xPercent: 0, opacity: 1, duration: this.textDuration }, '<+20%')
      .fromTo('.tmln-section-6 .tmln-brand span', { yPercent: 130 }, { yPercent: 0, stagger: 4, duration: 5 })
      .to('body', { duration: this.scrollTrigerStutter });


    // 1970's
    gsap.set('.tmln-section-7', { clearProps: true });
    gsap.set('.tmln-section-7 .tmln-year-cell', { clearProps: true });
    document.querySelectorAll('.tmln-section-7 .tmln-group').forEach((section) => {
      gsap.set(section, { clearProps: true });
    });

    gsap.timeline({
      scrollTrigger: {
        trigger: '.tmln-section-7',
        pin: '.tmln-section-7 .tmln-year-cell',
        start: 'top top+=45px',
        end: 'bottom-=260px top',
        onUpdate: self => this.updateYearProgress(document.querySelector('.tmln-section-7 .tmln-year-large'), 1970, 1978, self.progress),
      },
    });

    document.querySelectorAll('.tmln-section-7 .tmln-group').forEach((section) => {
      gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top bottom',
          end: 'center bottom',
          pin: false,
          scrub: 1,
        },
      })
        .fromTo(section, { yPercent: 20 }, { yPercent: 0, duration: 1.5 });
    });


    // 1980's
    gsap.set('.tmln-section-9', { clearProps: true });
    gsap.set('.tmln-section-9 .col-1 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-9 .col-2 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-9 .col-1 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-9 .col-2 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-9 .col-1 .barbie-box', { clearProps: true });
    gsap.set('.tmln-section-9 .col-1 p', { clearProps: true });
    gsap.set('.tmln-section-9 .col-1 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-9 .col-1 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-9 .col-2 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-9 .col-2 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-9 .col-2 p', { clearProps: true });

    gsap.timeline({ scrollTrigger: { trigger: '.tmln-section-9' } })
      // .fromTo('.tmln-section-9 .col-2 .img-mask', { x: '-100vw' }, { x: 0 }, 0)
      .fromTo('.tmln-section-9 .col-1 p', { yPercent: -100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration })
      .fromTo('.tmln-section-9 .col-1 .tmln-subhead', { yPercent: -100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .fromTo('.tmln-section-9 .col-1 .tmln-year', { yPercent: -100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .fromTo('.tmln-section-9 .col-2 .tmln-year', { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<')
      .fromTo('.tmln-section-9 .col-2 .tmln-subhead', { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .fromTo('.tmln-section-9 .col-2 p', { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .fromTo('.tmln-section-9 .col-1 .barbie-box', { opacity: 0 }, { opacity: 1, duration: this.duration / 2, stagger: this.duration / 2 }, '<+20%')
      .fromTo('.tmln-section-9 .col-2 .img-mask', { opacity: 0 }, { opacity: 1 }, '<+20%')
      .to('body', { duration: this.scrollTrigerStutter });


    // warhol
    gsap.set('.tmln-section-10', { clearProps: true });
    gsap.set('.tmln-section-10 .tmln-year span', { clearProps: true });
    gsap.set('.tmln-section-10 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-10 p', { clearProps: true });
    gsap.set('.tmln-section-10 .warhol-b-grid', { clearProps: true });
    gsap.set('.tmln-section-10 .warhol-headshot', { clearProps: true });
    gsap.set('.tmln-section-10 .warhol-quote', { clearProps: true });

    gsap.timeline({ scrollTrigger: { trigger: '.tmln-section-10' } })
      .fromTo('.tmln-section-10 .tmln-year span', { yPercent: -100 }, { yPercent: 0, duration: this.yearDuration })
      .fromTo('.tmln-section-10 .tmln-subhead', { xPercent: -20, opacity: 0 }, { xPercent: 0, opacity: 1, duration: this.textDuration }, '<+20%')
      .fromTo('.tmln-section-10 p', { xPercent: -20, opacity: 0 }, { xPercent: 0, opacity: 1, duration: this.textDuration }, '<+20%')
      // .fromTo('.tmln-section-10 .warhol-b-grid img', { scale: 0 }, { scale: 1, stagger: 2 })
      .fromTo('.tmln-section-10 .warhol-b-grid', { scale: 0 }, { scale: 1 })
      // .fromTo('.tmln-section-10 .warhol-b', { scale: 0 }, { scale: 1 })
      .fromTo('.tmln-section-10 .warhol-headshot', { x: '100vw', rotation: 120 }, { x: 0, rotation: 5 }, '<+20%')
      .fromTo('.tmln-section-10 .warhol-quote', { opacity: 0, yPercent: 20 }, { opacity: 1, yPercent: 0 }, '<+20%')
      .to('body', { duration: this.scrollTrigerStutter });


    // 1990's
    gsap.set('.tmln-section-12', { clearProps: true });
    gsap.set('.tmln-section-12 .col-1 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-12 .col-2 .img-mask', { clearProps: true });
    gsap.set('.tmln-section-12 .col-1 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-12 .col-2 .img-mask img', { clearProps: true });
    gsap.set('.tmln-section-12 .col-1 .img-mask .ann-bc-video', { clearProps: true });
    gsap.set('.tmln-section-12 .col-2 .img-mask .ann-bc-video', { clearProps: true });
    gsap.set('.tmln-section-12 .col-1 p', { clearProps: true });
    gsap.set('.tmln-section-12 .col-1 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-12 .col-1 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-12 .col-2 .tmln-year', { clearProps: true });
    gsap.set('.tmln-section-12 .col-2 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-12 .col-2 p', { clearProps: true });

    gsap.timeline({ scrollTrigger: { trigger: '.tmln-section-12' } })
      .fromTo('.tmln-section-12 .col-1 .img-mask', { x: '-100vw' }, { x: 0 })
      .fromTo('.tmln-section-12 .col-1 .img-mask .ann-bc-video', { scale: 4 }, { scale: 1 }, '<')
      .fromTo('.tmln-section-12 .col-2 .img-mask', { x: '100vw' }, { x: 0 }, '<')
      .fromTo('.tmln-section-12 .col-2 .img-mask img', { scale: 4 }, { scale: 1 }, '<')
      .fromTo('.tmln-section-12 .col-1 p', { yPercent: -100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+50%')
      .fromTo('.tmln-section-12 .col-1 .tmln-subhead', { yPercent: -100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .fromTo('.tmln-section-12 .col-1 .tmln-year', { yPercent: -100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .fromTo('.tmln-section-12 .col-2 .tmln-year', { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<')
      .fromTo('.tmln-section-12 .col-2 .tmln-subhead', { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .fromTo('.tmln-section-12 .col-2 p', { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration }, '<+5%')
      .to('body', { duration: this.scrollTrigerStutter });


    // 2010s
    gsap.set('.tmln-section-11', { clearProps: true });
    gsap.set('.tmln-section-11 .tmln-year-cell', { clearProps: true });
    document.querySelectorAll('.tmln-section-11 .tmln-group').forEach((section) => {
      gsap.set(section, { clearProps: true });
    });

    gsap.timeline({
      scrollTrigger: {
        trigger: '.tmln-section-11',
        pin: '.tmln-section-11 .tmln-year-cell',
        start: 'top top+=45px',
        end: 'bottom-=260px top',
        onUpdate: self => this.updateYearProgress(document.querySelector('.tmln-section-11 .tmln-year-large'), 2003, 2021, self.progress),
      },
    });

    document.querySelectorAll('.tmln-section-11 .tmln-group').forEach((section) => {
      gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top bottom',
          end: 'center bottom',
          pin: false,
          scrub: 1,
        },
      })
        .fromTo(section, { yPercent: 20 }, { yPercent: 0, duration: 1.5 });
    });


    // in good company
    gsap.set('.tmln-section-13', { clearProps: true });
    gsap.set('.tmln-section-13 .tmln-subhead', { clearProps: true });
    gsap.set('.tmln-section-13 p', { clearProps: true });
    gsap.set('.tmln-section-13 .catalog', { clearProps: true });
    gsap.set('.tmln-section-13 .ann-bc-video', { clearProps: true });
    gsap.set('.tmln-section-13 .nintendo', { clearProps: true });
    gsap.set('.tmln-section-13 .helmets', { clearProps: true });

    gsap.timeline({ scrollTrigger: { trigger: '.tmln-section-13' } })
      .fromTo('.tmln-section-13 .tmln-year', { yPercent: -100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: this.textDuration })
      .fromTo('.tmln-section-13 .tmln-subhead', { xPercent: 20, opacity: 0 }, { xPercent: 0, opacity: 1, duration: this.textDuration }, '<+20%')
      .fromTo('.tmln-section-13 p', { xPercent: 20, opacity: 0 }, { xPercent: 0, opacity: 1, duration: this.textDuration }, '<+20%')
      .fromTo('.tmln-section-13 .catalog', { yPercent: 200 }, { yPercent: 0 }, '<+20%')
      .fromTo('.tmln-section-13 .ann-bc-video', { xPercent: -200 }, { xPercent: 0 }, '<+20%')
      .fromTo('.tmln-section-13 .nintendo', { yPercent: -300 }, { yPercent: 0 }, '<+20%')
      .fromTo('.tmln-section-13 .helmets', { yPercent: 200 }, { yPercent: 0 }, '<+20%')
      .to('body', { duration: this.scrollTrigerStutter });
  }

  setAllAnimation() {
    // preload images
    document.querySelectorAll('.preload-section').forEach((section) => {
      ScrollTrigger.create({
        trigger: section,
        start: '-200% bottom',
        once: true,
        pin: false,
        onEnter: () => {
          // load images
          section.querySelectorAll('.preload-img').forEach((img) => {
            const newSrc = img.getAttribute('data-src');
            const newImg = document.createElement('img');
            newImg.onload = () => {
              img.src = newImg.src;
              // console.log(`150-preload-img-loaded: ${newImg.src}`);
            };
            newImg.src = newSrc;
          });

          // load videos
          section.querySelectorAll('.ann-bc-video').forEach((video) => {
            this.brightcove.getBrightcoveVideoData(video.getAttribute('data-video-id'))
              .then((r) => {
                this.videoLoaded(r.video, r.poster, video);
                // console.log(`150-preload-video-loaded: ${video.getAttribute('data-video-id')}`);
              })
              .catch(err => console.log(`150-preload-video-error: ${err}`));
          });
        },
      });
    });


    // baseball
    gsap.timeline({
      scrollTrigger: {
        trigger: '.tmln-section-3 .tmln-image-group',
        start: 'top bottom',
        end: 'bottom top',
        pin: false,
      },
    })
      .fromTo('.tmln-section-3 .tmln-baseball', { xPercent: 200, rotation: 0 }, { xPercent: -300, rotation: -180 });

    // crown
    gsap.timeline({
      scrollTrigger: {
        trigger: '.tmln-section-7 .tmln-image-group',
        start: 'top bottom',
        end: 'bottom top',
        pin: false,
      },
    })
      .fromTo('.tmln-section-7 .tmln-crown', { xPercent: -100 }, { xPercent: 200 });
  }

  initAnimation() {
    ScrollTrigger.matchMedia({
      '(max-width: 599px)': () => this.setMobileAnimation(),
      '(min-width: 600px)': () => this.setMediumAnimation(),
      all: () => this.setAllAnimation(),
    });
  }

  videoLoaded(video, poster, element) {
    element.innerHTML = `
    <video loop playsinline muted autoplay poster="${poster}">
      <source src="${video}" type="video/mp4">
    </video>
    `;
  }
}



// WEBPACK FOOTER //
// /opt/jenkins_home/workspace/marketing-campaigns_deploy_trigger_bcom/package/src/facts.js